import {
  Component,
  effect,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { isPermissionError, parseApiError } from '../../../quotes-api-error';
import { PricingModel } from '../../../../models/pricing-model.model';
import { QuoteRevisionItem } from '../../../../models/quote-revision-item.model';
import {
  PricingInformationEntry,
  PricingParameters,
  QuantityType,
  quantityToTime,
  timeToQuantity,
} from '../../../../models/quote-item-data.model';
import { QuantityPipe } from '../../../../pipes/quantity.pipe';
import { formatVolumeDisplayValue } from '../../../../utils/volume-format';
import { PricingModelService } from '../../../services/pricing-model.service';
import { QuoteRevisionItemService } from '../../../services/quote-revision-item.service';
import { ParameterEditorComponent } from '../../../shared/parameter-editor/parameter-editor.component';
import {
  buildSchemaBackedParameterValues,
  sanitizeParameterValuesForSave,
} from '../../../shared/parameter-editor/parameter-editor.utils';

interface OverrideDraft {
  unitCost: number | null;
  quantity: number | null;
  quantityHours: number | null;
  quantityMins: number | null;
  quantitySecs: number | null;
  note: string;
  error: string | null;
}

@Component({
  selector: 'app-quote-revision-item-pricing-information',
  imports: [
    CommonModule,
    FormsModule,
    Button,
    FloatLabelModule,
    InputNumberModule,
    InputTextModule,
    MessageModule,
    QuantityPipe,
    ParameterEditorComponent,
  ],
  providers: [CurrencyPipe],
  templateUrl: './pricing-information.component.html',
})
export class QuoteRevisionItemPricingInformationComponent implements OnChanges {
  readonly QuantityType = QuantityType;

  @Input() item!: QuoteRevisionItem;
  @Input() selectedCadObjectId: string | null = null;
  @Input({ required: true }) currencyCode!: string;

  private revisionItemService = inject(QuoteRevisionItemService);
  pricingModelService = inject(PricingModelService);
  private currencyPipe = inject(CurrencyPipe);

  pricingModel: PricingModel | null = null;
  editablePricingParameters: PricingParameters = {};
  pricingParametersChanged = false;
  savingPricingParameters = false;
  savePricingError: string | null = null;
  showRecalculatedMessage = false;

  // Manual price override state
  showOverridePanel = false;
  overrideDraft: OverrideDraft = {
    unitCost: null,
    quantity: null,
    quantityHours: 0,
    quantityMins: 0,
    quantitySecs: 0,
    note: '',
    error: null,
  };
  savingOverride = false;
  showOverrideSavedMessage = false;

  get pricingUnavailableReason(): string | null {
    if (!this.item) return null;

    if ((this.item.children?.length ?? 0) > 0) {
      return 'Pricing is calculated on leaf items only. This item has child items — check each child for its own pricing.';
    }

    if (this.getPricingGeometryForParameters().length === 0) {
      return 'No CAD object or drawing PDF is linked to this item. Link a CAD file or drawing to enable automatic pricing.';
    }

    if (!this.item.pricing_model_id) {
      return 'No pricing model is assigned to this item. Set a pricing model in the item editor to enable pricing.';
    }

    return null;
  }

  get pricingModelName(): string | null {
    return this.pricingModel?.name ?? this.item.pricing_model_name ?? null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.item) {
      this.loadPricingModelAndInitializeParameters();
    }
  }

  constructor() {
    effect(() => {
      this.pricingModelService.pricingModelsById();
      // ToDo: Convert this whole component use use signals, so this effect is no longer neeeded
      if (!this.item) return;
      this.loadPricingModelAndInitializeParameters();
    });
  }

  get selectedCadParameters(): Record<string, string | null> | null {
    if (!this.selectedCadObjectId) return null;
    return buildSchemaBackedParameterValues(
      this.parameterSchemas,
      this.editablePricingParameters[this.selectedCadObjectId],
    );
  }

  get parameterSchemas() {
    return this.pricingModel?.parameter_schemas ?? {};
  }

  get selectedCadInformation(): PricingInformationEntry | null {
    if (!this.selectedCadObjectId || !this.item.pricing_information)
      return null;
    return (
      this.item.pricing_information.find(
        (info) => info['cad_object_id'] === this.selectedCadObjectId,
      ) || null
    );
  }

  /**
   * Geometry identities for pricing parameters (CAD ids or drawing-{id}).
   * CAD (own then inherited) wins over drawing, matching backend resolution.
   */
  getPricingGeometryForParameters(): { id: string; name: string }[] {
    const ownCadObjects = this.item.cad_objects || [];
    if (ownCadObjects.length > 0) {
      return ownCadObjects.map((cadObject) => ({
        id: String(cadObject.id),
        name: cadObject.name || `CAD object ${cadObject.id}`,
      }));
    }

    const inheritedCadObjects = this.item.inherited_cad_objects || [];
    if (inheritedCadObjects.length > 0) {
      return inheritedCadObjects.map((cadObject) => ({
        id: String(cadObject.id),
        name: cadObject.name || `CAD object ${cadObject.id}`,
      }));
    }

    const ownDrawings = this.item.drawing_files || [];
    if (ownDrawings.length > 0) {
      return ownDrawings.map((drawing) => ({
        id: `drawing-${drawing.id}`,
        name: drawing.filename || `Drawing PDF ${drawing.id}`,
      }));
    }

    const inheritedDrawings = this.item.inherited_drawing_files || [];
    return inheritedDrawings.map((drawing) => ({
      id: `drawing-${drawing.id}`,
      name: drawing.filename || `Drawing PDF ${drawing.id}`,
    }));
  }

  async loadPricingModelAndInitializeParameters(): Promise<void> {
    if (!this.item.pricing_model_id) {
      this.pricingModel = null;
      return;
    }
    this.pricingModel =
      this.pricingModelService.pricingModelsById()[
        this.item.pricing_model_id
      ] || null;
    this.initializeEditablePricingParameters();
  }

  initializeEditablePricingParameters(): void {
    const geometryEntries = this.getPricingGeometryForParameters();
    const existingParams = this.item.pricing_parameters || {};

    this.editablePricingParameters = Object.fromEntries(
      geometryEntries.map((geometry) => {
        const geometryId = geometry.id;
        return [
          geometryId,
          buildSchemaBackedParameterValues(
            this.parameterSchemas,
            existingParams[geometryId],
          ),
        ];
      }),
    );

    this.pricingParametersChanged = false;
  }

  onParameterEditorChange(event: { key: string; value: string | null }): void {
    if (!this.selectedCadObjectId) return;
    this.editablePricingParameters[this.selectedCadObjectId] = {
      ...buildSchemaBackedParameterValues(
        this.parameterSchemas,
        this.editablePricingParameters[this.selectedCadObjectId],
      ),
      [event.key]: event.value,
    };
    this.pricingParametersChanged = true;
  }

  async savePricingParameters(): Promise<void> {
    if (this.savingPricingParameters) return;

    this.savingPricingParameters = true;
    this.savePricingError = null;

    const cleanedParams: PricingParameters = {};
    for (const [cadId, params] of Object.entries(
      this.editablePricingParameters,
    )) {
      cleanedParams[cadId] = sanitizeParameterValuesForSave(params);
    }

    try {
      await this.revisionItemService.updateItem(this.item.persistent_id, {
        pricing_parameters: cleanedParams,
      });
      this.initializeEditablePricingParameters();
      this.pricingParametersChanged = false;
      this.showRecalculatedMessage = true;
      setTimeout(() => (this.showRecalculatedMessage = false), 2000);
    } catch (error) {
      if (isPermissionError(error)) return;
      this.savePricingError = parseApiError(
        error,
        'An error occurred while saving pricing parameters.',
      );
      console.error('Error saving pricing parameters:', error);
      throw error;
    } finally {
      this.savingPricingParameters = false;
    }
  }

  get hasEditableParameters(): boolean {
    return Object.keys(this.parameterSchemas).length > 0;
  }

  /** Whether the attached pricing model produces a unit cost that can be overridden. */
  get supportsUnitCostOverride(): boolean {
    return this.pricingModel?.output_unit_cost ?? false;
  }

  /** Whether the attached pricing model produces a quantity that can be overridden. */
  get supportsQuantityOverride(): boolean {
    return this.pricingModel?.output_quantity ?? false;
  }

  get pricingResultModel(): PricingModel | null {
    const model = this.pricingModel;
    return model?.output_quantity || model?.output_unit_cost ? model : null;
  }

  get hasResultQuantity(): boolean {
    return this.pricingModel?.output_quantity ?? false;
  }

  get hasResultUnitCost(): boolean {
    return this.pricingModel?.output_unit_cost ?? false;
  }

  get isOverridden(): boolean {
    return (
      this.item?.unit_cost_override != null ||
      this.item?.quantity_override != null
    );
  }

  get isUnitCostOverridden(): boolean {
    return this.item?.unit_cost_override != null;
  }

  get isQuantityOverridden(): boolean {
    return this.item?.quantity_override != null;
  }

  /** Quantity to show as the primary Result value: the pricing model's own output when
   * available, otherwise the effective value. */
  get displayQuantity(): { quantity: number; quantity_type: QuantityType } {
    const calculated = this.item.calculated_quantity;
    return {
      quantity: calculated != null ? Number(calculated) : this.item.quantity,
      quantity_type: this.item.quantity_type,
    };
  }

  /** Overridden quantity to show beneath the calculated value, or null if not overridden. */
  get overriddenQuantity(): {
    quantity: number;
    quantity_type: QuantityType;
  } | null {
    const override = this.item.quantity_override;
    if (override == null) return null;
    return {
      quantity: Number(override),
      quantity_type: this.item.quantity_type,
    };
  }

  get overrideCallToActionLabel(): string {
    if (this.supportsQuantityOverride && !this.supportsUnitCostOverride) {
      return 'Quantity incorrect? Override here';
    }
    return 'Price incorrect? Override here';
  }

  openOverridePanel(): void {
    const quantity = this.toNumberOrNull(
      this.item.quantity_override ?? this.item.quantity,
    );
    const [quantityHours, quantityMins, quantitySecs] = quantityToTime(
      quantity ?? 0,
    );
    this.overrideDraft = {
      unitCost: this.toNumberOrNull(
        this.item.unit_cost_override ?? this.item.unit_cost,
      ),
      quantity,
      quantityHours,
      quantityMins,
      quantitySecs,
      note: this.item.override_note ?? '',
      error: null,
    };
    this.showOverridePanel = true;
  }

  onOverrideTimeFieldChange(): void {
    let hours = this.overrideDraft.quantityHours ?? 0;
    let mins = this.overrideDraft.quantityMins ?? 0;
    let secs = this.overrideDraft.quantitySecs ?? 0;

    if (secs >= 60 || mins >= 60) {
      mins += Math.floor(secs / 60);
      secs %= 60;
      hours += Math.floor(mins / 60);
      mins %= 60;
    }
    if (secs < 0 || mins < 0) {
      if (secs < 0) {
        mins -= 1;
        secs += 60;
      }
      if (mins < 0) {
        hours -= 1;
        mins += 60;
      }
    }
    if (hours < 0) {
      hours = 0;
      mins = 0;
      secs = 0;
    }

    this.overrideDraft.quantityHours = hours;
    this.overrideDraft.quantityMins = mins;
    this.overrideDraft.quantitySecs = secs;
    this.overrideDraft.quantity = timeToQuantity(hours, mins, secs);
  }

  cancelOverride(): void {
    this.showOverridePanel = false;
    this.overrideDraft.error = null;
  }

  async saveOverride(): Promise<void> {
    const unitCostOverride = this.toNumberOrNull(this.overrideDraft.unitCost);
    const quantityOverride = this.toNumberOrNull(this.overrideDraft.quantity);

    await this.persistOverride(
      {
        unit_cost_override:
          this.supportsUnitCostOverride && unitCostOverride != null
            ? unitCostOverride.toFixed(2)
            : null,
        quantity_override:
          this.supportsQuantityOverride && quantityOverride != null
            ? quantityOverride.toFixed(6)
            : null,
        override_note: this.overrideDraft.note?.trim() ?? '',
      },
      'An error occurred while saving the price override.',
      true,
    );
  }

  async clearOverride(): Promise<void> {
    await this.persistOverride(
      {
        unit_cost_override: null,
        quantity_override: null,
        override_note: '',
      },
      'An error occurred while reverting the price override.',
    );
  }

  private async persistOverride(
    patch: {
      unit_cost_override: string | null;
      quantity_override: string | null;
      override_note: string;
    },
    errorMessage: string,
    showSavedMessage: boolean = false,
  ): Promise<void> {
    if (this.savingOverride) return;
    this.savingOverride = true;
    this.overrideDraft.error = null;

    try {
      await this.revisionItemService.updateItem(this.item.persistent_id, {
        ...patch,
      });
      this.showOverridePanel = false;
      if (showSavedMessage) {
        this.showOverrideSavedMessage = true;
        setTimeout(() => (this.showOverrideSavedMessage = false), 2000);
      }
    } catch (error) {
      if (isPermissionError(error)) return;
      this.overrideDraft.error = parseApiError(error, errorMessage);
      console.error('Error persisting price override:', error);
      throw error;
    } finally {
      this.savingOverride = false;
    }
  }

  getInfoKeys(info: PricingInformationEntry): string[] {
    const exclude = [
      'cad_object_id',
      'cad_object_name',
      'Estimated Hours',
      'Material Volume',
      'Material Surface Area',
    ];
    return Object.keys(info).filter((key) => !exclude.includes(key));
  }

  /**
   * Format pricing value, converting hardcoded £ symbols to proper currency and keeping all other values unchanged.
   * This allows us to support legacy pricing information entries that have hardcoded £ values.
   */
  formatPricingValue(value: string | null): string {
    if (value === null) {
      return 'N/A';
    }
    const match = value.match(/^£([0-9.,]+)$/);
    if (match) {
      const numericValue = parseFloat(match[1].replace(/,/g, ''));
      if (!isNaN(numericValue)) {
        return (
          this.currencyPipe.transform(numericValue, this.currencyCode) || value
        );
      }
    }

    return formatVolumeDisplayValue(value) ?? value;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed =
      typeof value === 'number' ? value : Number.parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : null;
  }
}
